import { useRef, useState, useCallback, useEffect } from 'react';
import { flushSync } from 'react-dom';

/* Плитки-номера в шапке разбора (экран ДЗ/варианта и «Работа над ошибками»).
   Номер — подписью над цветным квадратиком. Плитка ВСЕГДА одного размера: от
   количества номеров зависит только сколько их помещается в ряд. Если рядов
   выходит больше двух — берём следующую ступень поменьше, но одинаково для всех.
   Ряды заполняем поровну: 15 → 8+7, а не 14+1. h — высота квадратика без подписи. */
const SLAT_STEPS = [
  { w: 46, h: 28, gap: 6, fs: 12.5 },
  { w: 41, h: 25, gap: 5, fs: 11.5 },
  { w: 36, h: 22, gap: 4, fs: 10.5 },
];

export const slatLayout = (total, boxW) => {
  const W = boxW || 700;                        // до первого замера — разумная ширина
  const n = Math.max(total, 1);
  for (let i = 0; i < SLAT_STEPS.length; i++) {
    const st = SLAT_STEPS[i];
    const fit = Math.max(1, Math.floor((W + st.gap) / (st.w + st.gap)));
    const rows = Math.ceil(n / fit);
    if (rows <= 2 || i === SLAT_STEPS.length - 1) return { ...st, cols: Math.ceil(n / rows) };
  }
};

export const slatVars = (l) => ({
  '--sw': `${l.w}px`, '--sh': `${l.h}px`, '--sg': `${l.gap}px`, '--sf': `${l.fs}px`,
  gridTemplateColumns: `repeat(${l.cols}, ${l.w}px)`,
});

/* Сворачивание шапки разбора: остаются кнопка «назад», плитки-номера и стрелка.
   Сворачивание: сначала гаснет остальное, потом шапка плавно меняет высоту,
   а каждая плитка перелетает на новое место (FLIP), даже если в ряду их стало
   больше. Разворачивание — в обратном порядке. Раскладку внутри шапки на время
   анимации фиксируем, иначе центрирование по высоте сдвигало бы плитки. */
export function useRbCollapse(setSlatBox) {
  const heroRef = useRef(null);
  const [min, setMin] = useState(false);
  const [fade, setFade] = useState(false);
  const busy = useRef(false);

  const toggle = useCallback(() => {
    const hero = heroRef.current;
    if (!hero || busy.current) return;
    busy.current = true;
    const collapsing = !min;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const DUR = 460;
    const EASE = 'cubic-bezier(.2,.8,.2,1)';

    const morph = () => {
      const slats = hero.querySelector('.rb-slats');
      const main = hero.querySelector('.rb-main');
      const top = hero.querySelector('.rb-top');
      const back = hero.querySelector('.rb-back');
      // Перелетают плитки и кнопка «назад»: она остаётся слева и в свёрнутой шапке
      const movers = [...(back ? [back] : []), ...(slats ? [...slats.children] : [])];
      const first = movers.map(t => t.getBoundingClientRect());
      const h0 = hero.getBoundingClientRect().height;

      flushSync(() => setMin(collapsing));
      if (slats) flushSync(() => setSlatBox(slats.clientWidth));
      if (reduce) { if (!collapsing) setFade(false); busy.current = false; return; }

      const heroBox = hero.getBoundingClientRect();
      const h1 = heroBox.height;
      const last = movers.map(t => t.getBoundingClientRect());
      // Пока высота шапки анимируется, раскладку внутри фиксируем на итоговых
      // местах: иначе центрирование по высоте двигало бы содержимое вместе с краем.
      const pinned = [];
      if (collapsing) {
        const padTop = parseFloat(getComputedStyle(hero).paddingTop) || 0;
        [main, top].forEach(el => {
          if (!el) return;
          const offset = el.getBoundingClientRect().top - heroBox.top - padTop;
          el.style.alignSelf = 'flex-start';
          el.style.marginTop = `${offset}px`;
          pinned.push(el);
        });
      } else if (main) {
        // Сначала меряем, потом снимаем flex: без него блок сжимается до содержимого,
        // и результат с кнопками ехал выше, а в конце анимации прыгал на место
        const mainH = main.getBoundingClientRect().height;
        main.style.flex = 'none';
        main.style.height = `${mainH}px`;
      }
      movers.forEach((t, i) => {
        const a = first[i], b = last[i];
        if (!a || !b || !b.width) return;
        t.style.transition = 'none';
        t.style.transformOrigin = 'top left';
        t.style.transform = `translate(${a.left - b.left}px, ${a.top - b.top}px) scale(${a.width / b.width}, ${a.height / b.height})`;
      });
      hero.style.transition = 'none';
      hero.style.height = `${h0}px`;
      hero.getBoundingClientRect();   // фиксируем стартовую точку анимации

      requestAnimationFrame(() => {
        hero.style.transition = `height ${DUR}ms ${EASE}`;
        hero.style.height = `${h1}px`;
        movers.forEach((t, i) => {
          t.style.transition = `transform ${DUR}ms ${EASE} ${Math.min(i * 8, 120)}ms`;
          t.style.transform = '';
        });
        if (!collapsing) setTimeout(() => setFade(false), 240);
        setTimeout(() => {
          hero.style.transition = ''; hero.style.height = '';
          if (main) { main.style.flex = ''; main.style.height = ''; }
          pinned.forEach(el => { el.style.alignSelf = ''; el.style.marginTop = ''; });
          movers.forEach(t => { t.style.transition = ''; t.style.transformOrigin = ''; });
          busy.current = false;
        }, DUR + 140);
      });
    };

    if (collapsing) {
      setFade(true);
      setTimeout(morph, reduce ? 0 : 170);
    } else {
      morph();
    }
  }, [min, setSlatBox]);

  return { heroRef, min, fade, toggle };
}

/* Переход к номеру по клику на плитку разбора. Тонкостей три:
   • ведём к карточке только после отрисовки — иначе высота считается по ещё
     свёрнутой карточке, и с первого клика номер открывался не целиком;
   • карточка раскрывается с анимацией, поэтому довыравниваем ещё дважды, когда
     высоты устаканились: без этого при быстрых кликах промахивало;
   • последним номерам не хватает места, чтобы встать под верх, — в конце списка
     держим распорку (.rb-tail) ровно нужной высоты. */
const scrollParent = (node) => {
  let el = node?.parentElement;
  while (el) {
    const oy = getComputedStyle(el).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 4) return el;
    el = el.parentElement;
  }
  return null;
};

export function useRbJump(jumpTo) {
  useEffect(() => {
    if (!jumpTo) return;
    const el = document.getElementById(`rb-row-${jumpTo}`);
    const list = scrollParent(el);
    if (!el || !list) return;

    // Распорка снизу — ровно столько, чтобы последняя карточка дошла до верха
    const sizeTail = () => {
      const tail = list.querySelector('.rb-tail');
      const cards = list.querySelectorAll('.rb-card, .rb-row');
      const last = cards[cards.length - 1];
      if (tail && last) {
        // с запасом на отступ снизу и зазор между карточками — иначе последний
        // номер упирается и не доходит до верха
        tail.style.height = `${Math.max(0, list.clientHeight - last.getBoundingClientRect().height + 8)}px`;
      }
    };
    // Насколько карточка стоит ниже верха области
    const gap = () => el.getBoundingClientRect().top - list.getBoundingClientRect().top - 4;

    sizeTail();
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      list.scrollTop += gap();
      return;
    }

    // Одно движение с разгоном и торможением. Цель пересчитываем каждый кадр:
    // карточка в это время ещё раскрывается, и список под ней едет — иначе в
    // конце был бы рывок. Длительность — от расстояния, чтобы близкий номер не
    // полз, а дальний не прыгал.
    const from = list.scrollTop;
    const dur = Math.min(620, Math.max(280, Math.abs(gap()) * 0.55));
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const began = performance.now();
    let raf = 0, stopped = false;
    const stop = () => { stopped = true; cancelAnimationFrame(raf); };
    const step = (now) => {
      if (stopped) return;
      // распорку по ходу движения не трогаем: её сжатие подрезает прокрутку
      // снизу, и на этом кадре был рывок
      const t = Math.min(1, (now - began) / dur);
      const target = list.scrollTop + gap();          // куда нужно с учётом текущей раскладки
      list.scrollTop = from + (target - from) * ease(t);
      if (t < 1) raf = requestAnimationFrame(step);
      else { sizeTail(); list.scrollTop += gap(); }   // доводка на полпикселя
    };
    raf = requestAnimationFrame(step);

    // Ученик тронул колесо — не тянем его обратно
    const opts = { passive: true };
    list.addEventListener('wheel', stop, opts);
    list.addEventListener('touchstart', stop, opts);
    return () => {
      stop();
      list.removeEventListener('wheel', stop, opts);
      list.removeEventListener('touchstart', stop, opts);
    };
  }, [jumpTo]);
}
